import { useEffect, useMemo, useState } from 'react'

import {
  Anchor,
  Box,
  Group,
  InputWrapper,
  NumberInput,
  Paper,
  Radio,
  Stack,
  Text,
} from '@mantine/core'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { usdFormatter } from '@repo/common/helpers/formatters'
import {
  useAdminFund,
  useAdminFundAssets,
} from '@repo/common/queries/admin/fund'
import { useCreateFundAssetTransaction } from '@repo/common/queries/admin/fund/mutation_create_fund_asset_transaction'
import { useFundTransactionOptions } from '@repo/common/queries/admin/fund/query_fund_transaction_options'

import { TowerCard } from '@/components/ui/TowerCard'
import { TowerDateTimeInput } from '@/components/ui/TowerDateTimeInput'

import { FundAssetSelector } from '../ui/FundAssetSelector'
import { TowerApprovalControl } from '../ui/TowerApprovalControl'
import { TowerAutocomplete } from '../ui/TowerAutocomplete'

interface TransactionRecordingFormProps {
  fundId: string
  initialSide?: 'buy' | 'sell'
  mode?: 'company' | 'external_fund' | 'any'
  initialAssetId?: string
  onAssetChange?: (assetId: string) => void
  onSideChange?: (side: 'buy' | 'sell') => void
}

export function TransactionRecordingForm({
  fundId,
  initialSide = 'buy',
  mode: propMode = 'any',
  initialAssetId,
  onAssetChange,
  onSideChange,
}: TransactionRecordingFormProps) {
  const { data: fund } = useAdminFund(fundId)
  const inputExecutedAt =
    fund?.feeders?.length && !fund.feeders.find((x) => x.tradable)
  const [mode, setMode] = useState<'company' | 'external_fund'>(
    propMode === 'any' ? 'external_fund' : propMode,
  )
  const [selectedAssetId, setSelectedAssetId] = useState<string>(
    initialAssetId || '',
  )
  const [executedAt, setExecutedAt] = useState<string | null>(null)
  const [showTimestampSection, setShowTimestampSection] = useState(false)
  const [side, setSide] = useState<string>(initialSide)
  const [shareCount, setShareCount] = useState<number | string>(0)
  const [totalPrice, setTotalPrice] = useState<number | string>(0)
  const [shareClass, setShareClass] = useState<string>('common')
  const [spv, setSpv] = useState<string>('')
  const [spvShareClass, setSpvShareClass] = useState<string>('')
  const [fees, setFees] = useState<number | string>('')
  const [formError, setFormError] = useState<string | null>(null)
  const [locked, setLocked] = useState(false)
  const navigate = useNavigate()

  const { mutateAsync, isPending, isError, error } =
    useCreateFundAssetTransaction()

  const { data: transactionOptions, isLoading: optionsLoading } =
    useFundTransactionOptions()

  const { data: fundAssetsData } = useAdminFundAssets(fundId)

  const selectedAsset = useMemo(() => {
    if (!fundAssetsData || !selectedAssetId) return null

    const allAssets = [
      ...fundAssetsData.assets.nodes,
      ...fundAssetsData.fund.assets.nodes.map((node) => node.asset),
    ]

    return allAssets.find((asset) => asset.id === selectedAssetId) || null
  }, [fundAssetsData, selectedAssetId])

  const shareClassOptions = [
    { value: 'common', label: 'Common' },
    { value: 'preferred', label: 'Preferred' },
    ...(transactionOptions?.fund_asset_transaction_share_classes || [])
      .filter((sc) => sc !== 'common' && sc !== 'preferred')
      .map((sc) => ({ value: sc, label: formatOptionLabel(sc) })),
  ]

  const spvOptions = [
    { value: '', label: 'None' },
    ...(transactionOptions?.fund_asset_transaction_spvs || [])
      .filter((s) => s) // Filter out empty/null values
      .map((s) => ({ value: s, label: s })),
  ]

  const spvShareClassOptions = [
    { value: '', label: 'None' },
    ...(transactionOptions?.fund_asset_transaction_spv_share_classes || [])
      .filter((sc) => sc) // Filter out empty/null values
      .map((sc) => ({ value: sc, label: formatOptionLabel(sc) })),
  ]

  function formatOptionLabel(value: string): string {
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const pricePerShare =
    typeof totalPrice === 'number' &&
    typeof shareCount === 'number' &&
    shareCount > 0
      ? totalPrice / shareCount
      : 0

  const pricePerSharePreFee =
    typeof totalPrice === 'number' &&
    typeof shareCount === 'number' &&
    shareCount > 0
      ? (totalPrice - (typeof fees === 'number' ? fees : 0)) / shareCount
      : 0

  // Auto-detect mode based on selected asset
  useEffect(() => {
    if (selectedAsset && propMode === 'any') {
      const assetMode =
        selectedAsset.category === 'company' ? 'company' : 'external_fund'
      if (mode !== assetMode) {
        setMode(assetMode)
      }
    }
  }, [selectedAsset, propMode, mode])

  const handleSubmit = async (): Promise<void> => {
    setFormError(null)

    if (!selectedAssetId) {
      setFormError(
        `${mode === 'external_fund' ? 'Fund' : 'Company'} selection is required`,
      )
      return
    }

    if (inputExecutedAt && showTimestampSection && !executedAt) {
      setFormError('Execution timestamp is required')
      return
    }

    if (typeof shareCount !== 'number' || shareCount <= 0) {
      setFormError('Share count must be a positive number')
      return
    }

    if (typeof totalPrice !== 'number' || totalPrice <= 0) {
      setFormError('Total price must be a positive number')
      return
    }

    await mutateAsync({
      fund_id: fundId,
      asset_id: selectedAssetId,
      share_count: shareCount,
      side: side,
      spv: spv || null,
      spv_share_class: spvShareClass || null,
      share_class: shareClass || 'common',
      fees: typeof fees === 'number' ? fees : null,
      total_price: totalPrice,
      executed_at: executedAt || null,
    })

    // synthetic wait to make sure backned fills in the corresponding fund transaction row
    setTimeout(() => {
      void navigate({ to: '/fund/$id', params: { id: fundId } })
    }, 2000)
  }

  const handleStartApprove = (): void => {
    setLocked(true)
  }

  const handleCancelApprove = (): void => {
    setLocked(false)
  }

  const resetToDefaultTimestamp = (): void => {
    setExecutedAt(null)
    setShowTimestampSection(false)
  }

  const handleAssetChange = (assetId: string): void => {
    setSelectedAssetId(assetId)
    onAssetChange?.(assetId)
  }

  const handleSideChange = (newSide: string): void => {
    setSide(newSide)
    onSideChange?.(newSide as 'buy' | 'sell')
  }

  const isFormValid =
    selectedAssetId &&
    (!inputExecutedAt || !showTimestampSection || executedAt) &&
    typeof shareCount === 'number' &&
    shareCount > 0 &&
    typeof totalPrice === 'number' &&
    totalPrice > 0

  return (
    <TowerCard
      title={`Record ${side === 'buy' ? 'Purchase' : 'Sale'}`}
      description="Record a new transaction for this fund's asset"
      icon={side === 'buy' ? <IconArrowDown /> : <IconArrowUp />}
    >
      <Stack>
        {propMode === 'any' && (
          <Radio.Group
            label="Asset Type"
            value={mode}
            onChange={(value) => {
              setMode(value as 'company' | 'external_fund')
              handleAssetChange('')
            }}
          >
            <Group mt="xs">
              <Radio
                value="external_fund"
                label="External Fund"
                disabled={locked || isPending}
              />
              <Radio
                value="company"
                label="Company"
                disabled={locked || isPending}
              />
            </Group>
          </Radio.Group>
        )}

        <Stack gap="0">
          <FundAssetSelector
            label={mode === 'external_fund' ? 'External Fund' : 'Company'}
            fundId={fundId}
            value={selectedAssetId}
            onChange={handleAssetChange}
            mode={mode}
            placeholder={`Select a ${mode === 'external_fund' ? 'fund' : 'company'}`}
            required
            disabled={locked || isPending}
          />
          <Text c="dimmed" m="0" size="sm">
            &nbsp;
            {selectedAssetId && `Fission Asset ID ${selectedAssetId}`}
          </Text>
        </Stack>

        <Radio.Group
          label="Transaction Type"
          value={side}
          onChange={handleSideChange}
        >
          <Group mt="xs">
            <Radio value="buy" label="Buy" disabled={locked || isPending} />
            <Radio value="sell" label="Sell" disabled={locked || isPending} />
          </Group>
        </Radio.Group>

        {inputExecutedAt && !showTimestampSection && (
          <InputWrapper
            label="Transaction Executed At"
            required
            description={
              <Text fw={500} size="sm">
                Current timestamp will be used unless modified. &nbsp;
                <Anchor
                  size="sm"
                  onClick={() => {
                    setShowTimestampSection(true)
                  }}
                  data-testid="modify-timestamp-link"
                  style={{ cursor: 'pointer' }}
                >
                  Modify Transaction Timestamp...
                </Anchor>
              </Text>
            }
          ></InputWrapper>
        )}

        {inputExecutedAt && showTimestampSection && (
          <Stack gap={0}>
            <TowerDateTimeInput
              label="Transaction Executed At"
              value={executedAt}
              data-testid="executed-at-input"
              onChange={setExecutedAt}
              required
              disabled={locked || isPending}
              description={
                <Text fw={500} size="sm">
                  Timestamp of the transaction execution
                </Text>
              }
            />
            <Anchor
              size="sm"
              onClick={resetToDefaultTimestamp}
              style={{ cursor: 'pointer' }}
            >
              Use Default/Current Timestamp
            </Anchor>
          </Stack>
        )}

        <TowerAutocomplete
          label="Share Class"
          value={shareClass}
          onChange={(value) => {
            setShareClass(value || 'common')
          }}
          options={shareClassOptions}
          disabled={locked || isPending || optionsLoading}
          placeholder={
            optionsLoading
              ? 'Loading share classes...'
              : 'Select or enter share class'
          }
          required
        />

        <Group grow align="flex-start">
          <TowerAutocomplete
            label="SPV (Optional)"
            placeholder="Enter SPV name"
            value={spv}
            onChange={setSpv}
            options={spvOptions}
            disabled={locked || isPending}
          />

          {spv ? (
            <TowerAutocomplete
              label="SPV Share Class (Optional)"
              value={spvShareClass}
              onChange={(value) => {
                setSpvShareClass(value)
              }}
              options={spvShareClassOptions}
              disabled={locked || isPending || optionsLoading}
              placeholder={
                optionsLoading
                  ? 'Loading SPV share classes...'
                  : 'Select or enter SPV share class'
              }
            />
          ) : (
            <div></div>
          )}
        </Group>

        <Paper withBorder p="sm" mt="md">
          <Stack>
            <NumberInput
              label="Total Price (Fees-Inclusive)"
              value={totalPrice}
              onChange={setTotalPrice}
              onFocus={() => {
                if (totalPrice === 0) {
                  setTotalPrice('')
                }
              }}
              required
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              prefix="$"
              size="md"
              min={0.01}
              disabled={locked || isPending}
              error={
                typeof totalPrice === 'number' && totalPrice <= 0
                  ? 'Total price must be positive'
                  : ''
              }
              description={
                <Text fw={500} size="sm">
                  This is the total amount{' '}
                  {side === 'buy' ? 'paid' : 'received'}, including all
                  transaction fees
                </Text>
              }
            />

            <NumberInput
              label="Share Count"
              value={shareCount}
              onChange={setShareCount}
              required
              min={0.01}
              step={0.01}
              decimalScale={2}
              disabled={locked || isPending}
              error={
                typeof shareCount === 'number' && shareCount <= 0
                  ? 'Share count must be positive'
                  : ''
              }
            />

            <NumberInput
              label="Fees (Optional)"
              value={fees}
              onChange={setFees}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator=","
              prefix="$"
              min={0}
              disabled={locked || isPending}
              description={
                <Text size="sm">
                  Transaction fees already included in total price (for
                  record-keeping purposes)
                </Text>
              }
            />

            <Group>
              <Stack gap="xxxs" style={{ width: '100%' }}>
                <Group>
                  <Text>Price-Per-Share:</Text>
                  <Text size="lg">{usdFormatter(pricePerShare)}</Text>
                </Group>

                <Group c="dimmed">
                  <Text>Price Per Share, pre-fee:</Text>
                  <Text>{usdFormatter(pricePerSharePreFee)}</Text>
                </Group>
              </Stack>
            </Group>
          </Stack>
        </Paper>

        {(formError || isError) && (
          <Text c="red" size="sm">
            {formError ||
              (error instanceof Error ? error.message : 'An error occurred')}
          </Text>
        )}

        <Box maw={400}>
          <TowerApprovalControl
            onApprove={handleSubmit}
            onStartApprove={handleStartApprove}
            onCancel={handleCancelApprove}
            disabled={!isFormValid || isPending}
            buttonText={`Record ${side.charAt(0).toUpperCase() + side.slice(1)}`}
            confirmationText={`Slide to confirm ${side} transaction`}
            successText="Transaction recorded successfully"
          />
        </Box>
      </Stack>
    </TowerCard>
  )
}
